import * as React from 'react';
import type { ReactNode } from 'react';
import { ErrorState, type ErrorSize } from '@/components/error-state';
import { classify } from '@/lib/failure';
import { reportRenderError } from '@/lib/rum';

// Its own class rather than the router's: that one stores the thrown value and
// asks whether it is truthy, so `throw undefined` reads as nothing caught, the
// children render again, throw again, and React unmounts the whole tree. A
// boolean cannot be falsy by accident.
class Contained extends React.Component<
  {
    resetKey: string | number;
    onCatch: (error: unknown, info: React.ErrorInfo) => void;
    fallback: (error: unknown, reset: () => void) => ReactNode;
    children: ReactNode;
  },
  { caught: boolean; error: unknown; resetKey: string | number }
> {
  override state = { caught: false, error: undefined as unknown, resetKey: this.props.resetKey };

  static getDerivedStateFromError(error: unknown) {
    return { caught: true, error };
  }

  static getDerivedStateFromProps(
    props: { resetKey: string | number },
    state: { caught: boolean; resetKey: string | number },
  ) {
    if (state.caught && state.resetKey !== props.resetKey) {
      return { caught: false, error: undefined, resetKey: props.resetKey };
    }
    return { resetKey: props.resetKey };
  }

  override componentDidCatch(error: unknown, info: React.ErrorInfo): void {
    this.props.onCatch(error, info);
  }

  private readonly reset = () => {
    this.setState({ caught: false, error: undefined });
  };

  override render(): ReactNode {
    return this.state.caught
      ? this.props.fallback(this.state.error, this.reset)
      : this.props.children;
  }
}

type BoundaryProps = {
  // Identifies this boundary in the error report. Prefixed to stay apart from
  // the whole-view catches, which use `route:`.
  where: `region:${string}`;
  // Changing this clears a caught error. Without it a region that failed once
  // stays failed for the life of the tab.
  resetKey: string | number;
  children: ReactNode;
} & (
  | {
      // Replaces the default screen, for a region needing its own copy or a
      // control the default cannot know about. `size` only shapes that screen,
      // so it means nothing alongside one.
      fallback: (error: unknown, reset: () => void) => ReactNode;
      size?: never;
    }
  | { fallback?: never; size: ErrorSize }
);

// Contains a render failure to the region that had it. Draw it around what lost
// meaning, never around a control someone needs to get out of what they are
// doing -- a fallback that replaces a control is worse than the failure.
export function Boundary({ where, resetKey, size, children, fallback }: BoundaryProps) {
  return (
    <Contained
      resetKey={resetKey}
      onCatch={(error, info) => reportRenderError(error, where, info)}
      fallback={(error, reset) =>
        fallback ? (
          fallback(error, reset)
        ) : (
          <ErrorState failure={classify(error)} size={size ?? 'panel'} onRetry={reset} />
        )
      }
    >
      {children}
    </Contained>
  );
}
